import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { ClipboardCheck } from 'lucide-react';
import moment from 'moment';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getAllPolicies } from '../../../../../api/requests';
import { getSelectedCountry } from '../../../../../states/slices/resourceSlice';
import useRoute from '../../../../../hooks/routesHook';
import { formatCurrencyAmount, getPolicyReviewStatus, getVehicleCoverTypeFromKey, getVehicleUsageFromKey } from '../../../../../utils/miscFunctions';
import PolicyStatus from '../../../../../components/elements/PolicyStatus';

interface AwaitingApprovalProps {
  /**
   * Optional override when an internal admin is inspecting a specific
   * insurer company; otherwise the call falls back to the country / company
   * context already enforced by the API's security filter.
   */
  insurerId?: number;
}

interface PolicyRow {
  policyNumber?: string;
  insurerPolicyNumber?: string;
  customerName?: string;
  firstName?: string;
  lastName?: string;
  createdOn?: string;
  reviewStage?: string;
  firstPaymentReceived?: boolean;
  make?: string;
  model?: string;
  registrationNumber?: string;
  year?: number;
  registrationYear?: number;
  coverType?: string;
  totalPremium?: number;
  outRightPremium?: number;
  currency?: string;
  vehicleUsage?: string;
  coverPeriod?: number;
}

/**
 * Insurer dashboard widget — "Awaiting your approval".
 *
 * Lists every policy awaiting this insurer's decision: those explicitly
 * submitted (reviewStage = IN_APPROVAL) plus paid "Pending Approval" policies
 * (NEW / DRAFT / DECLINED + firstPaymentReceived). Unpaid Drafts are excluded.
 * The whole queue is resolved server-side in one scoped call via
 * awaitingApproval=true, so the badge count and pagination are exact even for a
 * large backlog. Card chrome is intentionally similar to RenewalsWithin30Days.
 *
 * Backend scoping: relies on the existing /admin/policies/summary security
 * filter, which Hibernate-filters by the logged-in company. An insurer
 * therefore sees only policies their brokers have submitted for them.
 */
const AwaitingApproval: React.FC<AwaitingApprovalProps> = ({ insurerId }) => {
  const selectedCountry = useSelector(getSelectedCountry);
  const countryId = selectedCountry?.id ?? 1;
  const { getDashboardBase } = useRoute();
  // Heading → full approval queue; each row → that policy's detail page.
  const listHref = `${getDashboardBase()}/policies?type=motor&awaiting_approval=true`;
  const detailHref = (policyNumber?: string) =>
    `${getDashboardBase()}/policies/${policyNumber}?type=motor`;

  // The whole "awaiting approval" queue (IN_APPROVAL + paid Pending Approval)
  // is resolved server-side in one scoped call via awaitingApproval=true, so
  // the count and pagination are exact even for a large backlog. The card is a
  // preview: show the first PAGE_SIZE and surface the true total from the page.
  const PAGE_SIZE = 20;

  const { data, isLoading } = useQuery({
    queryKey: ['insurerAwaitingApproval', countryId, insurerId],
    queryFn: () =>
      getAllPolicies(
        0,
        PAGE_SIZE,
        countryId,
        undefined,
        undefined, // reviewStage — superseded by awaitingApproval
        undefined, undefined, undefined, undefined, undefined, undefined,
        undefined, undefined, undefined, undefined, undefined,
        insurerId,
        undefined, // branchId
        undefined, // isFleet
        true, // awaitingApproval
      ),
    enabled: !!countryId,
  });

  // getAllPolicies resolves the raw body { message, data: <Spring Page>, ... }.
  const pageData: any = data?.data ?? data ?? {};
  const policies: PolicyRow[] = pageData?.content ?? pageData?.policies ?? [];
  const totalCount: number = pageData?.totalElements ?? policies.length;

  const customerDisplayName = (row: PolicyRow): string => {
    if (row.customerName && row.customerName.trim().length > 0) return row.customerName;
    const first = row.firstName ?? '';
    const last = row.lastName ?? '';
    const combined = (first + ' ' + last).trim();
    return combined.length > 0 ? combined : 'Unknown customer';
  };

  const vehicleDisplay = (row: PolicyRow): string => {
    const parts = [row.make, row.model].filter((v) => v && v.toUpperCase() !== 'UNKNOWN');
    return parts.join(' ').trim() || '—';
  };

  const coverDisplay = (row: PolicyRow): string =>
    row.coverType ? (getVehicleCoverTypeFromKey(row.coverType)?.title ?? row.coverType) : '—';

  const usageDisplay = (row: PolicyRow): string =>
    row.vehicleUsage ? (getVehicleUsageFromKey(row.vehicleUsage)?.title ?? '') : '';

  // Every row in this queue is either explicitly submitted (In Approval) or a
  // paid Pending Approval policy — the raw reviewStage would show a bare "New"
  // for the paid NEW ones, so derive the spec label (mirrors Policy.isPendingApproval).
  const statusLabel = (row: PolicyRow): string => {
    if (row.reviewStage === 'IN_APPROVAL') return 'In Approval';
    const isPending = ['NEW', 'DRAFT', 'DECLINED'].includes(row.reviewStage ?? '') && Boolean(row.firstPaymentReceived);
    return isPending ? 'Pending Approval' : getPolicyReviewStatus(row.reviewStage ?? '');
  };

  const premiumDisplay = (row: PolicyRow): string => {
    const amount = row.totalPremium ?? row.outRightPremium;
    return amount != null && amount !== 0
      ? `${row.currency || 'GHS'} ${formatCurrencyAmount(amount)}`
      : '—';
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-5 dark:bg-darkBg dark:border-darkBg-border">
      <div className="flex items-center justify-between mb-4">
        <Link
          to={listHref}
          className="flex items-center gap-2 group focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-main rounded"
        >
          <ClipboardCheck className="h-5 w-5 text-primary-main" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-darkBg-mainText group-hover:text-primary-main">
            Awaiting your approval
          </h3>
        </Link>
        {!isLoading && (
          <Link
            to={listHref}
            aria-label={`View all ${totalCount} policies awaiting approval`}
            className="text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-main"
          >
            {totalCount}
          </Link>
        )}
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-8 bg-gray-100 dark:bg-darkBg-border rounded animate-pulse" />
          ))}
        </div>
      ) : policies.length === 0 ? (
        <p className="text-sm text-gray-500 py-8 text-center">
          No policies are currently awaiting your approval.
        </p>
      ) : (
        <div>
          {/* Column header — hidden on narrow screens where rows stack. */}
          <div className="hidden md:grid grid-cols-12 gap-3 px-2 pb-2 border-b border-gray-100 dark:border-darkBg-border text-[11px] font-medium uppercase tracking-wide text-gray-400">
            <div className="col-span-3">Policy / Customer</div>
            <div className="col-span-2">Vehicle</div>
            <div className="col-span-2">Cover</div>
            <div className="col-span-2">Premium</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-1 text-right">Submitted</div>
          </div>

          <div className="divide-y divide-gray-100 dark:divide-darkBg-border max-h-96 overflow-y-auto">
            {policies.slice(0, PAGE_SIZE).map((row, idx) => {
              const key = row.policyNumber ?? row.insurerPolicyNumber ?? String(idx);
              const vehicleSub = [row.registrationNumber, row.year || row.registrationYear]
                .filter(Boolean)
                .join(' · ');
              return (
                <Link
                  key={key}
                  to={detailHref(row.policyNumber ?? row.insurerPolicyNumber)}
                  className="grid grid-cols-2 md:grid-cols-12 gap-x-3 gap-y-1 items-center py-2.5 text-sm hover:bg-gray-50 dark:hover:bg-darkBg-border/40 -mx-2 px-2 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-main"
                >
                  {/* Policy / Customer */}
                  <div className="col-span-2 md:col-span-3 min-w-0">
                    <p className="font-medium text-gray-800 dark:text-darkBg-mainText truncate">
                      {row.policyNumber ?? row.insurerPolicyNumber ?? '—'}
                    </p>
                    <p className="text-xs text-gray-400 truncate">{customerDisplayName(row)}</p>
                  </div>

                  {/* Vehicle */}
                  <div className="md:col-span-2 min-w-0">
                    <p className="text-gray-700 dark:text-darkBg-mainText truncate">{vehicleDisplay(row)}</p>
                    <p className="text-xs text-gray-400 truncate">{vehicleSub || '—'}</p>
                  </div>

                  {/* Cover */}
                  <div className="md:col-span-2 min-w-0">
                    <p className="text-gray-700 dark:text-darkBg-mainText truncate">{coverDisplay(row)}</p>
                    {usageDisplay(row) && (
                      <p className="text-xs text-gray-400 truncate">{usageDisplay(row)}</p>
                    )}
                  </div>

                  {/* Premium — single clean line, left aligned */}
                  <div className="md:col-span-2 min-w-0">
                    <p className="font-medium text-gray-800 dark:text-darkBg-mainText truncate">
                      {premiumDisplay(row)}
                    </p>
                  </div>

                  {/* Status — its own column */}
                  <div className="md:col-span-2 min-w-0">
                    <PolicyStatus status={statusLabel(row)} classes="px-2 py-0.5 text-[11px] whitespace-nowrap" />
                  </div>

                  {/* Submitted */}
                  <div className="md:col-span-1 md:text-right min-w-0">
                    {row.createdOn && (
                      <>
                        <p className="text-xs text-gray-600 dark:text-gray-300 truncate">
                          {moment(row.createdOn).format('DD MMM YYYY')}
                        </p>
                        <p className="text-[11px] text-gray-400 truncate">{moment(row.createdOn).fromNow()}</p>
                      </>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>

          {totalCount > policies.length && (
            <Link
              to={listHref}
              className="block pt-3 text-center text-xs font-medium text-primary-main hover:underline"
            >
              View all {totalCount} requests
            </Link>
          )}
        </div>
      )}
    </div>
  );
};

export default AwaitingApproval;
